import MessageBanner from 'LuxKit/Banners/MessageBanner'
import React, { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import useContentTag from 'hooks/Content/useContentTag'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getContentTagIcon } from './contentTagIconUtils'

interface Props {
  slug: string
  /**
   * Kind for MessageBanner
   * @default 'promotional-secondary'
   */
  kind?: 'informational-primary' | 'informational-secondary' | 'promotional-primary' | 'promotional-primary-accent' | 'promotional-secondary' | 'success' | 'warning' | 'critical'
}

/**
 * Component that renders a ContentTag as a MessageBanner
 */
function ContentTagBanner({
  slug,
  kind = 'promotional-secondary',
}: Props) {
  const { contentTag } = useContentTag(slug)
  const history = useHistory()
  const location = useLocation()

  const icon = useMemo(() => {
    if (!contentTag) {
      return undefined
    }
    return getContentTagIcon(contentTag.fields.icon)
  }, [contentTag])

  // Extract tag name from slug (e.g., "search-banner-disney" -> "disney")
  const tagName = useMemo(() => {
    if (!slug.startsWith('search-banner-')) {
      return undefined
    }
    return slug.replace('search-banner-', '')
  }, [slug])

  const handleButtonClick = useCallback(() => {
    if (!tagName) {
      return
    }

    const currentSearch = location.search
    const currentTags = new URLSearchParams(currentSearch).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []

    // Add tag if it's not already present
    if (!tagsArray.includes(tagName)) {
      tagsArray.push(tagName)
    }

    const newSearch = setSearchParamValue(currentSearch, 'offerTags', tagsArray.join(','))
    history.push({
      ...location,
      search: newSearch,
    })
  }, [tagName, history, location])

  if (!contentTag) {
    return null
  }

  const { fields } = contentTag

  const primaryActionButtonProps = fields.linkText ? {
    children: fields.linkText,
    onClick: handleButtonClick,
  } : undefined

  return (
    <MessageBanner
      kind={kind}
      icon={icon}
      heading={fields.title}
      primaryActionButtonProps={primaryActionButtonProps}
    >
      {fields.subtitle}
    </MessageBanner>
  )
}
export default ContentTagBanner
